import React, { FC, ReactElement, useEffect, useState } from 'react'
import * as d3 from 'd3'
import * as topojson from 'topojson-client'
import styled from 'styled-components'

import ZoomInIcon from 'Assets/ZoomIn.svg'
import ZoomOutIcon from 'Assets/ZoomOut.svg'
import parsed from 'Assets/ch-combined.json'

const clamp = (num:number, min:number, max:number) => Math.min(Math.max(num, min), max);

export interface Props {
  propertyNameToCompare: string
  municipalityData: { bfsId: number; [key: string]: any }[]
  minValue: number
  maxValue: number
  year: number
  activeMunicipalityId?: number
  newActiveMunicipalityIdHandler: (newId: number) => void
  hoverHandler: (newData: any) => void
  zoomIntoActiveMunicipality: boolean
  colorPalette?: [string, string, string]
  activeStrokeWidth: string
  activeStrokeColor: string
  municipalityStrokeColor: string
  municipalityStrokeWidth: string
  municipalityFallbackFillColor: string
  hoverStrokeWidth: string
  hoverStrokeColor: string
  lakeFillColor: string
  lakeStrokeColor: string
  lakeStrokeWidth: string
  nationalBorderColor: string
  nationalBorderWidth: string
  cantonStrokeColor: string
  cantonStrokeWidth: string
  toolTipRef: React.RefObject<HTMLDivElement>
}
const ChoroplethMap: FC<Props> = ({
  municipalityData,
  propertyNameToCompare,
  minValue,
  maxValue,
  year,
  activeMunicipalityId,
  newActiveMunicipalityIdHandler,
  hoverHandler,
  zoomIntoActiveMunicipality,
  colorPalette,
  activeStrokeWidth,
  activeStrokeColor,
  municipalityStrokeColor,
  municipalityStrokeWidth,
  municipalityFallbackFillColor,
  hoverStrokeWidth,
  hoverStrokeColor,
  lakeFillColor,
  lakeStrokeColor,
  lakeStrokeWidth,
  nationalBorderColor,
  nationalBorderWidth,
  cantonStrokeColor,
  cantonStrokeWidth,
  toolTipRef,
}): ReactElement => {
  const [chTopoJSON, setChTopoJSON] = useState<any | null>()
  const [path, setPath] = useState<d3.GeoPath | null>()
  const [geoJsonMuni, setGeoJsonMuni] = useState<any | null>()
  const [isZoomedIn, setIsZoomedIn] = useState<boolean>(false)

  // Refs
  const countryBorderRef = React.useRef<SVGPathElement | null>(null)
  const municipalityLayerRef = React.useRef<SVGPathElement | null>(null)
  const highlightLayerRef = React.useRef<SVGPathElement | null>(null)
  const swissChoroplethMapRef = React.useRef<HTMLDivElement | null>(null)
  const svgRef = React.useRef<SVGSVGElement | null>(null)
  const defaultViewBox = '0 0 975 680'
  const tooltipDistanceToCursor = 10
  const color = d3
    .scaleLinear<string>()
    .domain([minValue, (minValue + maxValue) / 2, maxValue])
    .range(colorPalette || ['green', 'lightgrey', 'red'])
  function getValueToCompare(bfsIdToFind: number): number | undefined {
    const foundInMunicipaltyData = municipalityData.find(
      (muni) => +muni.bfsId === +bfsIdToFind
    )
    return foundInMunicipaltyData
      ? foundInMunicipaltyData[propertyNameToCompare]
      : undefined
  }
  function repositionToolTip(mouseX: number, mouseY: number) {
    const toolTipElement = d3.select(toolTipRef.current).node() as HTMLElement
    const widthOfTooltip = toolTipElement?.clientWidth
    const windowWidth = document.documentElement.clientWidth
    const toolTipShouldBeLeft =
      widthOfTooltip + mouseX + tooltipDistanceToCursor > windowWidth
    if (!toolTipShouldBeLeft) {
      d3.select(toolTipRef.current)
        .style('left', mouseX + tooltipDistanceToCursor + 'px')
        .style('top', mouseY + tooltipDistanceToCursor + 'px')
    } else {
      d3.select(toolTipRef.current)
        .style('left', mouseX - widthOfTooltip - tooltipDistanceToCursor + 'px')
        .style('top', mouseY + tooltipDistanceToCursor + 'px')
    }
  }
  function addHoverInteractions() {
    const mapElement = d3
      .select(swissChoroplethMapRef.current)
      .node() as HTMLElement

    if (mapElement?.clientWidth > 600) {
      d3.select(highlightLayerRef.current)
        .selectChildren('.municipality-highlight')
        // d3.selectAll('.municipality-highlight')

        .on('mouseover', function (event: MouseEvent) {
          d3.select(toolTipRef.current).style('opacity', 1)
          repositionToolTip(event.clientX, event.clientY)
          hoverHandler(
            municipalityData.find(
              (muni) => +muni.bfsId === +d3.select(this).attr('bfsId')
            )
          )
          d3.select(this)
            .transition()
            .duration(20)
            .style('stroke-width', hoverStrokeWidth)
            .style('stroke', hoverStrokeColor)
        })
        .on('mouseout', function () {
          d3.select(toolTipRef.current).style('opacity', 0)

          if (!(+d3.select(this).attr('bfsId') === activeMunicipalityId)) {
            d3.select(this)
              .transition()
              .duration(300)
              .style('stroke', 'transparent')
              .style('stroke-width', 'transparent')
          }
        })
        .on('mousemove', function (event: MouseEvent) {
          repositionToolTip(event.clientX, event.clientY)
        })
      let timer: NodeJS.Timeout
      window.addEventListener(
        'scroll',
        function () {
          d3.select(toolTipRef.current).style('opacity', 0)
        },
        false
      )
    }
  }
  function zoomTo(
    selection: d3.Selection<d3.BaseType, unknown, null, undefined>
  ) {
    const selectedMunicipalityElement = selection.node() as SVGGraphicsElement
    const distanceToObject = 40
    const bbox = selectedMunicipalityElement.getBBox()

    d3.select(svgRef.current)
      .transition()
      .ease(d3.easeQuadInOut)
      .duration(600)
      .attr(
        'viewBox',
        bbox.x -
          distanceToObject +
          ' ' +
          (bbox.y - distanceToObject) +
          ' ' +
          (bbox.width + distanceToObject * 2) +
          ' ' +
          (bbox.height + distanceToObject * 2)
      )
    setIsZoomedIn(true)
  }
  function zoomOut() {
    d3.select(svgRef.current)
      .transition()
      .ease(d3.easeQuadInOut)
      .duration(600)
      .attr('viewBox', defaultViewBox)
    setIsZoomedIn(false)
  }
  function zoomToActiveMunicipality(passedActiveMunicipalityId?: number) {
    const bfsIdToZoomTo = passedActiveMunicipalityId || activeMunicipalityId
    if (bfsIdToZoomTo) {
      console.log('zoomToActiveMunicipality', {bfsIdToZoomTo});
      
      const activeMunicipalitySelection = d3
        .select(highlightLayerRef.current)
        .selectChild('#bfsId-' + bfsIdToZoomTo)
      activeMunicipalitySelection.node() && zoomTo(activeMunicipalitySelection)
    }
  }
  function highlightActiveMunicipality() {
    const muniToHighlight = d3
      .select(highlightLayerRef.current)
      .selectChild('#bfsId-' + activeMunicipalityId)
    if (activeMunicipalityId && muniToHighlight.node()) {
      muniToHighlight
        .transition()
        .duration(20)
        .style('stroke-width', activeStrokeWidth)
        .style('stroke', activeStrokeColor)

      addHoverInteractions()
      isZoomedIn && zoomTo(muniToHighlight)
    }
  }
  function addMunicipalityPaths() {
    if (path) {
      d3.select(municipalityLayerRef.current)
        .selectAll('path')
        .data(geoJsonMuni.features)
        .enter()
        .append('path')
        .attr('d', (feature: any) => path(feature))
        .attr('class', 'municipality')
        .attr('stroke', municipalityStrokeColor)
        .attr('stroke-width', municipalityStrokeWidth)
        .attr('fill', (feature: any) => {
          const valueToCompare = getValueToCompare(feature.id)
          return valueToCompare !== undefined
            ? color(clamp(valueToCompare, minValue, maxValue))
            : municipalityFallbackFillColor
        })
    }
  }

  useEffect(() => {
    // fetch('https://unpkg.com/swiss-maps/' + year + '/ch-combined.json').then(
    //   (res) =>
    //     res.json().then((parsed) => {
          // convert topojson back to geojson

          setGeoJsonMuni(
            topojson.feature(parsed as any, parsed?.objects?.municipalities as any)
          )
          setChTopoJSON(parsed)
          const projection = d3
            .geoMercator()
            .rotate([0, 0])
            .center([8.23, 46.885])
            .scale(11980)
            .translate([975 / 2, 610 / 2])
            .precision(0.1)
          setPath(() => d3.geoPath(projection))
    //     })
    // )
       // Add eventListener on the window to the bfsIdChanged event
       function handleBfsIdChanged(event: CustomEvent) {
          if(event.detail && municipalityData.find(muni => muni.bfsId == event.detail)?.[propertyNameToCompare] ){
            zoomToActiveMunicipality(event.detail)
          }
      }
      window.addEventListener(
        'changeGDE',
        handleBfsIdChanged as EventListener
      )
      console.log('useEffect: []')
      return () => {
        window.removeEventListener(
          'changeGDE',
          handleBfsIdChanged as EventListener
        )
      }
  }, [])
  useEffect(() => {
    if (geoJsonMuni && path) {
      d3.select(highlightLayerRef.current)
        .selectAll('path')
        .data(geoJsonMuni.features)
        .enter()
        .append('path')
        .attr('d', (feature: any) => path(feature))
        .attr('class', 'municipality-highlight')
        .attr('stroke', 'transparent')
        .attr('strokeLinejoin', 'round')
        .attr('strokeLinecap', 'round')
        .attr('fill', 'transparent')
        .attr('id', (feature: any) => 'bfsId-' + feature.id)
        .attr('bfsId', (feature: any) => feature.id)
        .on('click', (event: PointerEvent, feature: any) => {
          if (window.innerWidth > 600) {
            newActiveMunicipalityIdHandler(feature.id)
          }
        })
      if (activeMunicipalityId) {
        highlightActiveMunicipality()
      } else {
        addHoverInteractions()
      }

      addMunicipalityPaths()
    }
    activeMunicipalityId && zoomToActiveMunicipality()

    console.log('useEffect: [geoJsonMuni, path]')
  }, [geoJsonMuni, path])

  const colorMunicipalities = () => {
    d3.select(municipalityLayerRef.current)
      .selectAll('path')
      .attr('fill', (feature: any) => {
        const valueToCompare = getValueToCompare(feature.id)
        return valueToCompare !== undefined
          ? color(clamp(valueToCompare, minValue, maxValue))
          : municipalityFallbackFillColor
      })
  }

  useEffect(() => {
    d3.select(highlightLayerRef.current)
      .selectChildren('.municipality-highlight')
      .transition()
      .duration(300)
      .style('stroke', 'transparent')
    highlightActiveMunicipality()
    console.log('useEffect: [activeMunicipalityId]')
  }, [activeMunicipalityId])

  useEffect(() => {
    colorMunicipalities()
    addHoverInteractions()
    console.log('useEffect: [municipalityData]')
  }, [municipalityData])

  return (
    <div>
      <div id='swiss-choropleth-map' ref={swissChoroplethMapRef}>
        {chTopoJSON && path && municipalityData && (
          <svg viewBox={defaultViewBox} ref={svgRef}>
            <g
              id='municipality-layer'
              ref={municipalityLayerRef}
              strokeLinejoin='round'
            />
            <g
              fill='none'
              stroke='#000'
              strokeLinejoin='round'
              strokeLinecap='round'
            >
              <path
                strokeWidth={cantonStrokeWidth}
                stroke={cantonStrokeColor}
                d={
                  path(
                    topojson.mesh(
                      chTopoJSON,
                      chTopoJSON.objects.cantons,
                      (a, b) => a !== b
                    )
                  ) as string
                }
              />
              <path
                strokeWidth={nationalBorderWidth}
                id='country-border'
                ref={countryBorderRef}
                stroke={nationalBorderColor}
                d={
                  path(
                    topojson.feature(chTopoJSON, chTopoJSON.objects.country)
                  ) as string
                }
              />
              <path
                fill={lakeFillColor}
                stroke={lakeStrokeColor}
                strokeWidth={lakeStrokeWidth}
                d={
                  path(
                    topojson.feature(chTopoJSON, chTopoJSON.objects.lakes)
                  ) as string
                }
              />
            </g>
            <g
              id='highlight-layer'
              ref={highlightLayerRef}
              strokeLinejoin='round'
            />
          </svg>
        )}
        <MapControls id='map-controls'>
          {isZoomedIn ? (
            <img src={ZoomOutIcon} alt='Zoom Out' onClick={() => zoomOut()} />
          ) : activeMunicipalityId ? (
            <img
              src={ZoomInIcon}
              alt='Zoom In'
              onClick={() => {
                setIsZoomedIn(true)
                zoomToActiveMunicipality()
              }}
            />
          ) : (
            ''
          )}
        </MapControls>
      </div>
    </div>
  )
}

const MapControls = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-right: 1rem;
  padding-bottom: 1.5rem;
  pointer-events: none;
  transform: translateY(-100%);
  img {
    transition: all 0.3s ease-in-out;
    &:hover {
      background-color: var(--gray-5);
    }
    @keyframes slide-in-fwd-center {
      0% {
        transform: translateZ(-1400px);
        opacity: 0;
      }
      100% {
        transform: translateZ(0) translate(0, -100%);
        opacity: 1;
      }
    }
    animation: slide-in-fwd-center 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)
      both;
    transform: translate(0, -200%);
    pointer-events: all;
    position: absolute;
    cursor: pointer;
    background-color: var(--gray-4);
    border-radius: 10%;
  }
`
export default React.memo(
  ChoroplethMap,
  (prevProps, nextProps) =>
    prevProps.municipalityData === nextProps.municipalityData &&
    prevProps.activeMunicipalityId === nextProps.activeMunicipalityId &&
    prevProps.minValue === nextProps.minValue &&
    prevProps.maxValue === nextProps.maxValue &&
    prevProps.year === nextProps.year

  // && prevProps.newActiveMunicipalityIdHandler === nextProps.newActiveMunicipalityIdHandler
)
